import { useContext, useEffect, useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import { Row, Col, Form, Input, Button, message, Modal, Spin } from "antd-v4";
import { LoadingOutlined } from "@ant-design/icons";

// => Context
import { FirebaseContext } from "../../context/firebaseContext";
import logoLight from "../../assets/images/logo/logo-light.png";
import logoDark from "../../assets/images/logo/logo-dark.png";
import leedpulse from "../../configs/leedpulse";

import { isEmailValid } from "../../utils/Utils";
import { useCustomise } from "../../context/customiseContext";

export default function Login() {
  // Context
  const { theme } = useCustomise();
  const { signInUser, cleanUp_UI, isAutoConnecting, autoConnectError } = useContext(FirebaseContext);
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const redirectTo = decodeURIComponent(searchParams.get("redirect") || "/");


  const onSubmit = async () => {
    // Check email is not empty & valid
    if (
      email === "" ||
      email === undefined ||
      email === null ||
      !isEmailValid(email)
    ) {
      const err = "Veuillez entrer une adresse email valide";
      setError(err);
      message.error(err);
      return;
    }

    // Check password not empty
    if (password === "" || password === undefined || password === null) {
      const err = "Veuillez entrer un mot de passe valide";
      setError(err);
      message.error(err);
      return;
    }

    setLoading(true);
    setError(null);

    const data = {
      email,
      password,
    };
    signInUser(data).then((res) => {
      setLoading(false);

      // Success
      if (res && res.status === "success") {
        message.success(res.msg || "Connexion réussie");
        navigate(redirectTo, { replace: true });
      }

      // Error
      if (res && res.status === "error") {
        let errorMessage = "Email ou mot de passe incorrect";

        if (res.msg && (
          res.msg.toLowerCase().includes("password") ||
          res.msg.toLowerCase().includes("mot de passe") ||
          res.msg.toLowerCase().includes("user") ||
          res.msg.toLowerCase().includes("utilisateur") ||
          res.msg.toLowerCase().includes("auth/") ||
          res.msg.toLowerCase().includes("invalid") ||
          res.msg.toLowerCase().includes("incorrect") ||
          res.msg.toLowerCase().includes("wrong") ||
          res.msg.toLowerCase().includes("not found") ||
          res.msg.toLowerCase().includes("non trouvé") ||
          res.msg.toLowerCase().includes("credentials") ||
          res.msg.toLowerCase().includes("identifiants")
        )) {

          errorMessage = "Email ou mot de passe incorrect";
        } else if (res.msg && res.msg.toLowerCase().includes("too-many-requests")) {
          errorMessage = "Trop de tentatives de connexion. Veuillez réessayer plus tard.";
        } else if (res.msg && res.msg.length > 0) {
          errorMessage = "Une erreur s'est produite lors de la connexion. Veuillez réessayer plus tard.";
        }

        setError(errorMessage);
        message.error(errorMessage);
      }
    }).catch(err => {
      setLoading(false);
      setError("Une erreur s'est produite lors de la connexion. Veuillez réessayer plus tard.");
      message.error("Une erreur s'est produite lors de la connexion. Veuillez réessayer plus tard.");
    });
  };

  const handleResetPassword = async () => {
    // Check email is not empty & valid
    if (
      resetEmail === "" ||
      resetEmail === undefined ||
      resetEmail === null ||
      !isEmailValid(resetEmail)
    ) {
      message.error("Veuillez entrer une adresse email valide");
      return;
    }

    setResetPasswordLoading(true);

    try {
      // await sendPasswordResetEmail(auth, resetEmail);
      await fetch(leedpulse.route_url + '/api/v1/auth/password-reset/leedpulse', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: resetEmail,
        }),
      });


      message.success("Un email de réinitialisation a été envoyé à votre adresse email");
      setResetPasswordModalVisible(false);
      setResetEmail("");
    } catch (error: any) {
      let errorMessage = "Une erreur s'est produite. Veuillez réessayer plus tard.";

      if (error.code === "auth/user-not-found") {
        errorMessage = "Aucun compte n'est associé à cette adresse email";
      } else if (error.code === "auth/too-many-requests") {
        errorMessage = "Trop de tentatives. Veuillez réessayer plus tard.";
      }

      message.error(errorMessage);
    } finally {
      setResetPasswordLoading(false);
    }
  };

  useEffect(() => void cleanUp_UI(), []);

  // If auto-connect is in progress, display a loading indicator
  if (isAutoConnecting) {
    return (
      <Row justify="center" align="middle" style={{ height: '100vh' }}>
        <Col span={24} style={{ textAlign: 'center' }}>
          <img
            src={theme === "light" ? logoLight : logoDark}
            alt="Logo LeedPulse"
            style={{ width: "250px", marginBottom: "20px" }}
          />
          <div style={{ marginTop: '40px' }}>
            <Spin size="large" />
            <p style={{ marginTop: '20px', fontSize: '16px' }}>Connexion automatique en cours...</p>
          </div>
        </Col>
      </Row>
    );
  }

  return (
    <Row justify="center" className="hp-authentication-page">
      <Col lg={12} span={24} className="hp-py-sm-0 hp-py-md-64">
        <Row className="hp-h-100" align="middle" justify="center">
          <Col
            xxl={11}
            xl={15}
            lg={20}
            md={20}
            sm={24}
            className="hp-px-sm-8 hp-pt-24 hp-pb-48"
          >
            <Col >
              <img
                src={theme === "light" ? logoLight : logoDark}
                alt="Logo LeedPulse"
                style={{ width: "334px" }}
              />
            </Col>

            <span className="hp-text-color-black-80 hp-text-color-dark-20">
              Entrez vos identifiants pour accéder à votre compte.
            </span>

            {autoConnectError && (
              <div className="hp-mt-16 hp-mb-8" style={{ color: 'red' }}>
                {autoConnectError}
              </div>
            )}

            <Form
              layout="vertical"
              name="basic"
              initialValues={{ remember: true }}
              className="hp-mt-sm-16 hp-mt-32"
            >
              <Form.Item label="Email :" className="hp-mb-16">
                <Input id="email"
                  value={email}
                  onChange={(e) => {
                    // Remove spaces from e.target.value
                    e.target.value = e.target.value.replace(/\s/g, '');
                    setEmail(e.target.value);
                  }}
                />
              </Form.Item>

              <Form.Item label="Mot de passe :" className="hp-mb-8">
                <Input.Password
                  id="password"
                  value={password}
                  onChange={(e) => {
                    // Remove spaces
                    e.target.value = e.target.value.replace(/\s/g, '');
                    setPassword(e.target.value)
                  }}
                  style={theme === "light" ? {borderColor: "#EFEBFF"} : {borderColor: "#292157"}}
                />
              </Form.Item>

              <Row align="middle" justify="space-between">
                <Button
                  type="link"
                  className="hp-text-color-black-80 hp-text-color-dark-40 hp-p-0"
                  onClick={() => setResetPasswordModalVisible(true)}
                >
                  Mot de passe oublié ?
                </Button>
              </Row>

              {error !== null && (
                <Row
                  className="hp-mt-16 hp-mb-8"
                  align="middle"
                  justify="space-between"
                >
                  <span className="hp-text-color-danger-1 hp-caption">
                    {error}
                  </span>
                </Row>
              )}

              <Form.Item className="hp-mt-16 hp-mb-8">
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  onClick={onSubmit}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <LoadingOutlined style={{ marginRight: '8px' }} /> Connexion...
                    </>
                  ) : (
                    "Se connecter"
                  )}
                </Button>
              </Form.Item>
            </Form>

            <Col className="hp-mt-sm-48 hp-mt-96">
              <p className="hp-p1-body hp-text-center hp-text-color-black-800 hp-text-color-dark-20 hp-mb-8">
                Leedpulse • Tous droits réservés
              </p>

              <Row align="middle" justify="center" gutter={[16, 0]}>
              <Col>
                <a
                  className="hp-p1-body hp-text-color-black-80 hp-hover-text-color-primary-1"
                  target="_blank"
                  href="https://www.leedpulse.com/mentions-legales" rel="noreferrer"
                >
                  Mentions légales
                </a>
              </Col>

              <Col>
                <a
                  className="hp-p1-body hp-text-color-black-80 hp-hover-text-color-primary-1"
                  target="_blank"
                  href="https://www.leedpulse.com/politique-de-confidentialite" rel="noreferrer"
                >
                  Politique de confidentialité
                </a>
              </Col>

              <Col>
                <a
                  className="hp-p1-body hp-text-color-black-80 hp-hover-text-color-primary-1"
                  target="_blank"
                  href="https://www.leedpulse.com/conditions-generales-dutilisation" rel="noreferrer"
                >
                  Conditions générales d'utilisation
                </a>
              </Col>
              </Row>
            </Col>
          </Col>
        </Row>
      </Col>

      <Modal
        title="Réinitialisation du mot de passe"
        open={resetPasswordModalVisible}
        onCancel={() => setResetPasswordModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setResetPasswordModalVisible(false)}>
            Annuler
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={resetPasswordLoading}
            onClick={handleResetPassword}
          >
            Envoyer
          </Button>,
        ]}
      >
        <p>Entrez votre adresse email pour recevoir un lien de réinitialisation de mot de passe</p>
        <Form layout="vertical">
          <Form.Item>
            <Input
              value={resetEmail}
              onChange={(e) => {
                e.target.value = e.target.value.replace(/\s/g, '');
                setResetEmail(e.target.value);
              }}
              placeholder="adresse@email.com"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Row>
  );
}
